import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { insights as insightsMock, publications as pubsMock } from "../mock/mock";
import { useCollection } from "../hooks/useCollection";

const tags = ["All", "AI Leadership", "DevSecOps", "Engineering Culture", "GenAI"];

export default function Insights() {
  const { data: insights } = useCollection("insights", insightsMock);
  const { data: publications } = useCollection("publications", pubsMock);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let list = insights;
    if (active !== "All") list = list.filter((i) => i.tag === active);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (i) =>
          i.title.toLowerCase().includes(s) ||
          i.excerpt.toLowerCase().includes(s) ||
          i.tag.toLowerCase().includes(s)
      );
    }
    return list;
  }, [active, q, insights]);

  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO — 7/5 split with featured post card */}
      <section className="pt-10 md:pt-14 pb-12 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-stretch">
            {/* LEFT */}
            <div className="col-span-12 md:col-span-7">
              <div className="tag-mono mb-6">[ Insights · Writing ]</div>
              <h1
                style={{
                  fontFamily: "Inter",
                  fontWeight: 800,
                  fontSize: "clamp(2.8rem, 6vw, 6.2rem)",
                  lineHeight: 0.94,
                  letterSpacing: "-0.045em",
                }}
              >
                Notes from the field on AI, leadership, and{" "}
                <span className="italic-serif text-[var(--molten)]">systems that scale.</span>
              </h1>
              <p className="mt-8 md:mt-10 text-lg md:text-xl text-[var(--ink)]/75 max-w-xl leading-relaxed">
                Practical frameworks distilled from years of building, leading, and shipping. No hype. No hot takes. Just what works.
              </p>
            </div>

            {/* RIGHT — featured latest post card */}
            <div className="col-span-12 md:col-span-5 flex flex-col gap-0">
              <div className="tag-mono mb-4">[ Most recent ]</div>
              {insights[0] ? (
                <article className="flex-1 p-7 md:p-8 bg-[var(--ink)] text-[var(--cream)] grain flex flex-col justify-between min-h-[320px]">
                  <div>
                    <div className="flex items-center justify-between mb-5">
                      <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/60">{insights[0].tag}</span>
                      <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/60">
                        {insights[0].date} · {insights[0].read_time || insights[0].read}
                      </span>
                    </div>
                    <h2
                      style={{
                        fontFamily: "Inter",
                        fontWeight: 700,
                        fontSize: "clamp(1.6rem, 2.4vw, 2.4rem)",
                        lineHeight: 1.02,
                        letterSpacing: "-0.03em",
                      }}
                      className="text-[var(--cream)]"
                    >
                      {insights[0].title}
                    </h2>
                    <p className="mt-5 text-[var(--cream)]/70 leading-relaxed max-w-md">
                      {insights[0].excerpt}
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-2 font-mono text-[0.68rem] uppercase tracking-[0.18em] text-[var(--molten)]">
                    Featured <ArrowUpRight size={14} />
                  </div>
                </article>
              ) : (
                <div className="flex-1 p-8 border border-[var(--ink)]/15 italic-serif text-2xl text-[var(--ink)]/50">
                  First post is coming soon.
                </div>
              )}
              <div className="grid grid-cols-3 gap-0 border-t border-[var(--ink)]/15 mt-5">
                <div className="py-5 px-4 border-r border-[var(--ink)]/15">
                  <div className="tag-mono mb-1">Published</div>
                  <div className="font-serif text-xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{insights.length}</div>
                </div>
                <div className="py-5 px-4 border-r border-[var(--ink)]/15">
                  <div className="tag-mono mb-1">Topics</div>
                  <div className="font-serif text-xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{tags.length - 1}</div>
                </div>
                <div className="py-5 px-4">
                  <div className="tag-mono mb-1">Cadence</div>
                  <div className="font-serif text-xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Monthly</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="border-y border-[var(--ink)]/15 grain bg-[var(--cream-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <button
                key={t}
                onClick={() => setActive(t)}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.12em] border transition-colors ${
                  active === t
                    ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]"
                    : "bg-transparent text-[var(--ink)]/70 border-[var(--ink)]/20 hover:border-[var(--ink)]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 bg-[var(--cream)] border border-[var(--ink)]/20 rounded-full px-4 py-2 w-full md:w-80">
            <Search size={16} className="text-[var(--ink)]/50" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search insights..."
              className="bg-transparent outline-none text-sm w-full placeholder:text-[var(--ink)]/40"
            />
          </label>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="py-16 md:py-20 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {filtered.length === 0 ? (
            <div className="py-24 text-center text-[var(--ink)]/60 italic-serif text-2xl">
              Nothing here yet. Try another filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[var(--ink)]/15">
              {filtered.map((p, i) => (
                <article
                  key={i}
                  className={`rise p-8 md:p-10 border-b border-[var(--ink)]/15 group cursor-pointer hover:bg-[var(--cream-soft)] transition-colors ${
                    i % 2 === 0 ? "md:border-r" : ""
                  }`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="tag-mono">{p.tag}</span>
                    <span className="tag-mono">{p.date} · {p.read}</span>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl leading-tight group-hover:text-[var(--molten)] transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-4 text-[var(--ink)]/70 leading-relaxed max-w-xl">{p.excerpt}</p>
                  <div className="mt-8 flex items-center gap-2 tag-mono group-hover:text-[var(--molten)] transition-colors">
                    Read the piece <ArrowUpRight size={14} />
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PUBLICATIONS */}
      <section className="section-ink grain py-24 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-4">[ Published research ]</div>
          <h2 className="headline-lg text-[var(--cream)] max-w-3xl mb-10">
            Peer-reviewed and indexed <span className="italic-serif text-[var(--molten)]">contributions.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {publications.map((p, i) => (
              <a
                key={i}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="rise p-6 border border-[var(--cream)]/20 hover:border-[var(--molten)] transition-colors group"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="tag-mono">{p.venue}</span>
                  <ArrowUpRight size={16} className="text-[var(--cream)]/50 group-hover:text-[var(--molten)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-[var(--cream)] leading-snug">{p.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
